import Offer from "domain/offer";
import { useMemo, useReducer, useState } from "react";
import * as _ from "lodash";


export enum OfferField {
    Type,
    CustomerAccount,
    CustomerWill,
    Commodity,
    ContractLocation,
    Location,
    DeliveryMethod,
    DeliveryPeriod,
    StartDate,
    EndDate,
    Symbol,
    Basis,
    SetBasis,
    OrderType,
    CashPrice,
    Quantity,
    Expiration,
    Trader,
    Comment,
    DeliveryTerms,
    Region,
    DPContract,
    FreightFee,
    HtaFee,
    Fees,
    PriceAtOpen,
    FuturesPrice,
    AtMarket,
    TasFallbackOption,
    PaymentOption,
    ContractSubType,
    InterestStartDate,
    PayDate,
    CustomerContractId,
    NotifyCustomer,
    PlaceHedge,
    DisableErpSync,
    InterestRate
}

interface ReducerParams {
    field: OfferField,
    newState: Offer,
    form: OfferFormState
}

type ChildReducer = (state: Offer, form: OfferFormState, previousState: Offer) => Offer;

export interface OfferFormState {
    allowChangingPrice?: boolean;
    placeHedgeOrder?: boolean;
    hasUserEnabledHedgeOrder?: boolean;
    hasUserUpdatedCashPrice?: boolean;
    shouldShowHedgeOrderToggle?: boolean;
    isPricingDelayedPriceContract?: boolean;
    lockDeliveryMethod?: boolean;
}

export interface ReducerDefinition {
    reducer: ChildReducer;
    fields: OfferField[];
    runOnInitialization?: boolean;
    excludeForAdds?: boolean;
    excludeForUpdates?: boolean;
}


export default function useOfferBuilder(defaultOffer: Offer, reducers: ReducerDefinition[], initialState: OfferFormState)
{
    const reducerCollection = useMemo(() => {
        return _.groupBy(
            reducers
                .flatMap(def => def.fields.map(f => ({key: f as number, reducer: def.reducer }))),
            m => m.key);
    }, [reducers]);

    const [formState, setFormState] = useState<OfferFormState>(initialState);

    const initialized = useMemo(() => {
        const params: ReducerParams = { newState: defaultOffer, form: formState, field: null};
        return runReducers(params, reducers.filter(m => m.runOnInitialization).map(m => m.reducer), defaultOffer);
    }, [defaultOffer])

    const [offer, dispatch] = useReducer(mainOfferReducer, initialized);


    function mainOfferReducer(oldState: Offer, params: ReducerParams): Offer {
        const reducers = reducerCollection[params.field];

        //If we don't have special handling for the update, use the passed state
        if (reducers == null) return params.newState;

        return runReducers(params, reducers.map(m => m.reducer), oldState)
    }

    function runReducers(params: ReducerParams, reducers: ChildReducer[], oldState: Offer): Offer {
        let s = params.newState;
        for (let r of reducers) {
            const newState = r(s, params.form, oldState);
            s = newState;
        }
        setFormState(params.form);
        return s;
    } 

    function updateOffer(field: OfferField | null, val: Offer, newFormState?: OfferFormState) {
        dispatch({
            field,
            newState: val,
            form: {...formState, ...newFormState}
        })
    }

    function updateOfferField(field: OfferField, key: keyof Offer, val: any, newFormState?: OfferFormState) {
        dispatch({
            field,
            newState: {
                ...offer,
                [key]: val
            },
            form: {...formState, ...newFormState}
        })
    }

    return {
        offer,
        updateOffer,
        updateOfferField,
        formState,
        updateForm: setFormState
    }

}
